import { Subject } from 'rxjs';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Component, Input, ElementRef, Optional, Self, OnDestroy, DoCheck, ViewChild, ChangeDetectorRef, OnInit } from '@angular/core';

@Component({
  selector: 'app-cuit-input',
  templateUrl: './cuit-input.component.html',
  styleUrls: ['./cuit-input.component.scss'],
  providers: [
      { provide: MatFormFieldControl, useExisting: CuitInputComponent }
  ],
  // tslint:disable-next-line: no-host-metadata-property
  host: {
      '[class.floating]': 'shouldLabelFloat',
      '[id]': 'id',
      '[attr.aria-describedby]': 'describedBy',
  }
})
export class CuitInputComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnInit, OnDestroy, DoCheck {

    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_disabled: boolean | string | null | undefined;
    // tslint:disable-next-line: variable-name
    static ngAcceptInputType_required: boolean | string | null | undefined;

    stateChanges = new Subject<void>();
    focused = false;
    errorState = false;
    controlType = 'app-cuit-input';
    describedBy = '';
    private placeholderValue = '';
    private requiredValue = false;
    private disabledValue = false;
    private valueValue = '';
    visibleValue = '';

    get empty() {
        return !this.value;
    }

    get shouldLabelFloat() { return this.focused || !this.empty; }

    @Input()
    id: string;

    @Input()
    get placeholder(): string { return this.placeholderValue; }
    set placeholder(value: string) {
        this.placeholderValue = value;
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean { return this.requiredValue; }
    set required(value: boolean) {
        this.requiredValue = value;
        this.stateChanges.next();
    }

    @Input()
    get value(): string {
        return this.valueValue;
    }
    set value(newValue: string | undefined) {
        this.valueValue = String(newValue) ?? '';
        this.visibleValue = this.getVisibleValue(this.valueValue);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean { return this.disabledValue; }
    set disabled(value: boolean) {
        this.disabledValue = value;
        this.stateChanges.next();
    }

    @ViewChild('input', { static: true })
    inputRef: ElementRef<HTMLInputElement>;

    constructor(
        private focusMonitor: FocusMonitor,
        private elementRef: ElementRef<HTMLElement>,
        @Optional() @Self() public ngControl: NgControl
    ) {

        focusMonitor.monitor(elementRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    onChange = (_: any) => {};
    onTouched = () => {};

    ngOnInit() {
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this.elementRef);
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.inputRef.nativeElement.focus();
        }
    }

    writeValue(obj: string) {
        if (obj !== null && obj !== undefined) {
            this.value = obj;
        }
    }

    registerOnChange(fn: (p: string) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    ngDoCheck() {
        if (this.ngControl) {
            this.errorState = this.ngControl.invalid && this.ngControl.touched;
            this.stateChanges.next();
        }
    }

    getVisibleValue(value: string) {
        const separator = ' - ';
        let visibleValue = '';
        if (value.length > 2) {
            visibleValue += value.substring(0, 2);
            visibleValue += separator;
            if (value.length > 10) {
                visibleValue += value.substring(2, 10);
                visibleValue += separator;
                visibleValue += value.substring(10);
            } else {
                visibleValue += value.substring(2);
            }
        } else {
            visibleValue += value;
        }
        return visibleValue;
    }

    onInputChange(event: InputEvent) {
        const visibleValue = (event.currentTarget as HTMLInputElement).value;
        const newValue = visibleValue.replace(/\D+/g, '');
        this.value = newValue;
        this.onChange(this.value);
        if (event.inputType === 'deleteContentBackward' || event.inputType === 'deleteContentForward') {
            const selectionStart = this.inputRef.nativeElement.selectionStart;
            const selectionEnd = this.inputRef.nativeElement.selectionEnd;
            setTimeout(() => {
                this.inputRef.nativeElement.selectionStart = selectionStart;
                this.inputRef.nativeElement.selectionEnd = selectionEnd;
            });
        }
    }

    onClick(event: Event) {
        this.inputRef.nativeElement.selectionStart = this.inputRef.nativeElement.value.length;
        this.inputRef.nativeElement.selectionEnd = this.inputRef.nativeElement.value.length;
        event.preventDefault();
    }

    onKeyDown(event: KeyboardEvent) {
        const availableKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace', 'Delete', 'Tab'];
        if (!availableKeys.includes(event.key)) {
            event.preventDefault();
        }
    }
}
